import { z } from "zod";
import { isoDateSchema, uuidSchema } from "./common";

export const mtmAssessmentTypeSchema = z.enum([
  "INITIAL_MTM",
  "FOLLOW_UP",
  "POST_DISCHARGE_REVIEW",
  "POLYPHARMACY_REVIEW",
  "CHRONIC_MEDICATION_MANAGEMENT",
]);

export const medicationQuestionnaireSchema = z.object({
  takesMedicationsAsPrescribed: z.boolean().optional(),
  missesDoses: z.boolean().optional(),
  missedDoseFrequency: z.enum(["RARELY", "SOMETIMES", "OFTEN", "DAILY"]).optional(),
  usesPillOrganizer: z.boolean().optional(),
  experiencesSideEffects: z.boolean().optional(),
  sideEffectsDescription: z.string().trim().max(1000).optional(),
  hasDifficultyAffordingMedications: z.boolean().optional(),
  hasDifficultySwallowingOrAdministering: z.boolean().optional(),
  usesOtherPharmacies: z.boolean().optional(),
  otherNotes: z.string().trim().max(1000).optional(),
});

export type MedicationQuestionnaireInput = z.infer<typeof medicationQuestionnaireSchema>;

export const startMtmAssessmentSchema = z.object({
  patientId: uuidSchema,
  appointmentId: uuidSchema.optional(),
  type: mtmAssessmentTypeSchema,
});

export const adherenceAssessmentSchema = z.object({
  level: z.enum(["HIGH", "MODERATE", "LOW", "UNKNOWN"]),
  barriers: z.array(z.string().trim().max(200)).max(20).optional(),
  notes: z.string().trim().max(1000).optional(),
});

export const updateMtmAssessmentSchema = z.object({
  clinicalSummary: z.string().trim().max(5000).optional(),
  adherenceAssessment: adherenceAssessmentSchema.optional(),
  status: z.enum(["DRAFT", "IN_PROGRESS", "PENDING_REVIEW", "COMPLETED", "CANCELLED"]).optional(),
});

export const mrpCategorySchema = z.enum(["INDICATION", "EFFECTIVENESS", "SAFETY", "ADHERENCE"]);

export const mrpSubTypeSchema = z.enum([
  "UNNECESSARY_THERAPY",
  "NEEDS_ADDITIONAL_THERAPY",
  "WRONG_DRUG",
  "DOSE_TOO_LOW",
  "INEFFECTIVE_THERAPY",
  "ADVERSE_DRUG_REACTION",
  "DOSE_TOO_HIGH",
  "DRUG_INTERACTION",
  "ALLERGY_CONTRAINDICATION",
  "NON_ADHERENCE",
  "ADMINISTRATION_DIFFICULTY",
  "COST_BARRIER",
  "OTHER",
]);

export const createMrpSchema = z.object({
  mtmAssessmentId: uuidSchema,
  category: mrpCategorySchema,
  subType: mrpSubTypeSchema,
  severity: z.enum(["LOW", "MODERATE", "HIGH", "CRITICAL"]),
  relatedPatientMedicationId: uuidSchema.optional(),
  relatedPatientMedicationId2: uuidSchema.optional(),
  description: z.string().trim().min(1, "Describe the medication-related problem").max(2000),
  descriptionAr: z.string().trim().max(2000).optional(),
});

export type CreateMrpInput = z.infer<typeof createMrpSchema>;

export const resolveMrpSchema = z.object({
  status: z.enum(["UNDER_REVIEW", "RESOLVED", "MONITORING", "NOT_APPLICABLE"]),
  resolutionNotes: z.string().trim().max(2000).optional(),
});

export const interventionTypeSchema = z.enum([
  "PATIENT_EDUCATION",
  "MEDICATION_ADJUSTMENT_RECOMMENDATION",
  "PRESCRIBER_COMMUNICATION",
  "ADHERENCE_SUPPORT",
  "REFERRAL",
  "MONITORING_RECOMMENDATION",
  "OTHER",
]);

export const createInterventionSchema = z.object({
  mtmAssessmentId: uuidSchema,
  mrpId: uuidSchema.optional(),
  type: interventionTypeSchema,
  recommendation: z.string().trim().min(1, "Document the recommendation").max(2000),
  recommendationAr: z.string().trim().max(2000).optional(),
  rationale: z.string().trim().max(2000).optional(),
  communicatedToPrescriber: z.boolean().default(false),
});

export type CreateInterventionInput = z.infer<typeof createInterventionSchema>;

export const monitoringParameterSchema = z.object({
  parameter: z.string().trim().min(1).max(200),
  parameterAr: z.string().trim().max(200).optional(),
  targetValue: z.string().trim().max(200).optional(),
  frequency: z.enum(["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY", "QUARTERLY", "AS_NEEDED"]),
  relatedConditionId: uuidSchema.optional(),
  relatedPatientMedicationId: uuidSchema.optional(),
  notes: z.string().trim().max(500).optional(),
});

export const medicationActionItemSchema = z.object({
  instruction: z.string().trim().min(1).max(500),
  instructionAr: z.string().trim().max(500).optional(),
  relatedPatientMedicationId: uuidSchema.optional(),
  dueDate: isoDateSchema.optional(),
});

export const createCarePlanSchema = z.object({
  mtmAssessmentId: uuidSchema,
  summary: z.string().trim().min(1, "Summarize the care plan").max(3000),
  summaryAr: z.string().trim().max(3000).optional(),
  monitoringParameters: z.array(monitoringParameterSchema).max(50).default([]),
  medicationActionPlan: z.array(medicationActionItemSchema).max(50).default([]),
  patientEducationTopics: z.array(z.string().trim().max(300)).max(30).optional(),
  patientEducationTopicsAr: z.array(z.string().trim().max(300)).max(30).optional(),
});

export type CreateCarePlanInput = z.infer<typeof createCarePlanSchema>;

export const scheduleFollowUpSchema = z.object({
  mtmAssessmentId: uuidSchema,
  carePlanId: uuidSchema.optional(),
  dueDate: isoDateSchema,
  reason: z.string().trim().max(500).optional(),
});

export type ScheduleFollowUpInput = z.infer<typeof scheduleFollowUpSchema>;

export const completeFollowUpSchema = z.object({
  outcomeNotes: z.string().trim().min(1, "Document the follow-up outcome").max(2000),
  outcomeMtmAssessmentId: uuidSchema.optional(),
});
